/**
 * Payments (section `payments`, paid events only — plan 6.H).
 * Provider overview · Price · Payment timing · Items & add-ons · Options · Cancellation, instructions & refunds.
 *
 * Every field is a `details` (paid details page) field; the `payments[]` rows are derived from
 * `event_prepaid_status / payments_* / deposit_*` by `payloads.buildPaymentsRows` (the 6.H table).
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FEATURE_IDS } from "../../../../../constants/featureAccess";

import { DowngradeNote, FieldError, Helper, PlanBadge, SettingLine, Switch } from "../../ui/primitives";
import { useBuilder } from "../../state/builderContext";
import { D, E, FLAT_PERCENT, PAYMENT_TYPE, REFUND_CUTOFF_OPTIONS, REFUND_TYPE, ROUTES, STEPS } from "../../state/constants";
import { currency, price as priceOf } from "../../state/selectors";
import SettingsView from "../shared/SettingsView";
import { PolicyFields } from "../invitee/InviteeSection";
import { providerRows } from "../quick/PaymentBlock";
import { paymentTiming } from "../quick/useQuickSetup";

// ---- helpers --------------------------------------------------------------------------------------------------

const issueFor = (issues, field) => {
  const i = (issues || []).find((x) => x.field === field);
  return i ? i.message : null;
};
const touched = (state) => state.save.version[STEPS.DETAILS] > 0 || !state.meta.isNew;
const money = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : 0);

const Section = ({ id, title, text, action, status, children, className = "" }) => (
  <div className={`eb-paid-section ${className}`.trim()} id={id}>
    <div className="eb-paid-section-head">
      <div>
        <h2>{title}</h2>
        {text ? <p>{text}</p> : null}
      </div>
      {status ? <span className="eb-paid-status">{status}</span> : null}
      {action || null}
    </div>
    {children}
  </div>
);

const usePayments = () => {
  const ctx = useBuilder();
  const { state, update } = ctx;
  const d = state.details;
  const set = (patch) => update(STEPS.DETAILS, patch);

  const setTiming = (t) => {
    if (t === "at_event") {
      set({ event_prepaid_status: D });
      return;
    }
    const base = { event_prepaid_status: E, payments_type: d.payments_type || PAYMENT_TYPE.FULL };
    if (t === "deposit") {
      set({
        ...base,
        deposit_type: PAYMENT_TYPE.DEPOSIT,
        deposit_flat_per_type: d.deposit_flat_per_type || FLAT_PERCENT.PERCENT,
        deposit_price: d.deposit_price || "",
      });
      return;
    }
    // Full: keeps the Partial switch as it is (6.H table rows 1–2)
    set({ ...base, deposit_type: "", deposit_price: "" });
  };

  const setPartial = (on) =>
    set(
      on
        ? { payments_type: PAYMENT_TYPE.PARTIAL, payments_flat_type: d.payments_flat_type || FLAT_PERCENT.PERCENT }
        : { payments_type: PAYMENT_TYPE.FULL, payments_price: "" }
    );

  const setPricing = (pricing) => set({ pricing });
  const setAddOns = (add_ons) => set({ add_ons });

  return { ...ctx, d, set, setTiming, setPartial, setPricing, setAddOns };
};

// ---- Provider overview -------------------------------------------------------------------------------------------------

const ProviderOverview = () => {
  const { state } = useBuilder();
  const providers = providerRows(state.lookups.connectedVenues);
  const [chosen, setChosen] = useState("");
  const shown = providers.includes(chosen) ? chosen : providers[0] || "";
  if (!providers.length && state.lookups.connectedVenuesLoaded) {
    return (
      <div className="eb-paid-overview" id="provider-setting">
        <div>
          <span className="eb-payment-brand">!</span>
          <div>
            <strong>No payment account connected.</strong>
            <p>
              Paid bookings cannot be collected until a gateway is connected. <Link to={ROUTES.PAYMENT_INTEGRATIONS}>Connect in Integrations</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="eb-paid-overview" id="provider-setting">
      <div>
        <span className={`eb-payment-brand ${shown === "PayPal" ? "paypal" : ""}`.trim()}>{shown === "PayPal" ? "P" : "S"}</span>
        <div>
          <strong>{shown ? `${shown} connected` : "Checking payment account\u2026"}</strong>
          <p>Payments will be deposited into your connected account.</p>
        </div>
      </div>
      <select value={shown} onChange={(e) => setChosen(e.target.value)} aria-label="Payment provider" disabled={providers.length <= 1}>
        {providers.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    </div>
  );
};

// ---- Price + timing --------------------------------------------------------------------------------------------------------

const PriceSection = () => {
  const { state, d, setPricing, issues } = usePayments();
  const cur = currency(state);
  const first = (d.pricing || [])[0] || { item_name: state.type.event_name, price: "", qty: 1 };
  const err = touched(state) ? issueFor(issues, "price") : null;
  return (
    <Section id="price-setting" title="Price" text="The amount invitees pay for this event." status="Required">
      <div className="eb-price-row">
        <label>
          Event price
          <div className={`eb-money-field ${err ? "eb-has-error" : ""}`.trim()}>
            <span>{cur}</span>
            <input
              type="number"
              min="1"
              step="0.01"
              value={first.price === undefined ? "" : first.price}
              onChange={(e) => setPricing([{ ...first, price: e.target.value }, ...(d.pricing || []).slice(1)])}
              aria-label="Event price"
            />
          </div>
          <FieldError message={err} />
        </label>
      </div>
    </Section>
  );
};

const TimingSection = () => {
  const { state, d, set, setTiming, issues } = usePayments();
  const cur = currency(state);
  const p = priceOf(state) || 0;
  const timing = paymentTiming(d);
  const partialOn = d.event_prepaid_status === E && d.payments_type === PAYMENT_TYPE.PARTIAL;
  const isPercent = (d.deposit_flat_per_type || FLAT_PERCENT.PERCENT) === FLAT_PERCENT.PERCENT;
  const dep = Number(d.deposit_price) || 0;
  const due = isPercent ? money((p * dep) / 100) : money(dep);
  const remaining = money(Math.max(p - due, 0));
  const depositError = touched(state) ? issueFor(issues, "deposit_price") : null;
  const providersConnected = state.lookups.paymentConnected !== false;

  const options = [
    { key: "full", title: "Full payment", sub: partialOn ? "Partial payment set below" : `${p ? `${p} ${cur}` : "Full amount"} due at booking` },
    { key: "deposit", title: "Deposit", sub: isPercent ? "Collect a percentage now" : "Collect a fixed amount now" },
    { key: "at_event", title: "Pay at event", sub: "No online payment required" },
  ];

  return (
    <Section id="payment-timing-setting" title="Payment timing" text="Choose how much is due when the invitee books.">
      <div className="eb-payment-timing wide" role="radiogroup" aria-label="Payment timing">
        {options.map((o) => {
          // 6.H table: "Full payment + Partial on" is stored as a partial_payment row → the Full card is not shown selected
          const selected = timing === o.key && !(o.key === "full" && partialOn);
          const disabled = o.key !== "at_event" && !providersConnected;
          return (
            <button type="button" key={o.key} role="radio" aria-checked={selected} className={selected ? "selected" : ""} disabled={disabled} onClick={() => setTiming(o.key)}>
              <i />
              <span>
                <strong>{o.title}</strong>
                <small>{o.sub}</small>
              </span>
            </button>
          );
        })}
      </div>
      {!providersConnected ? <Helper>Prepayment needs a connected payment account.</Helper> : null}

      {timing === "deposit" ? (
        <div className="eb-deposit-detail">
          <label>
            {isPercent ? "Deposit percentage" : `Deposit amount (${cur})`}
            <div>
              <input
                type="number"
                min="1"
                max={isPercent ? 99 : undefined}
                step={isPercent ? 1 : 0.01}
                value={d.deposit_price === undefined ? "" : d.deposit_price}
                onChange={(e) => set({ deposit_price: e.target.value })}
                aria-label={isPercent ? "Deposit percentage" : "Deposit amount"}
                className={depositError ? "eb-has-error" : ""}
              />
              <span>{isPercent ? "%" : cur}</span>
            </div>
            <div className="eb-view-toggle" role="radiogroup" aria-label="Deposit type" style={{ marginTop: 6 }}>
              <button type="button" className={isPercent ? "selected" : ""} onClick={() => set({ deposit_flat_per_type: FLAT_PERCENT.PERCENT })}>
                %
              </button>
              <button type="button" className={!isPercent ? "selected" : ""} onClick={() => set({ deposit_flat_per_type: FLAT_PERCENT.FLAT })}>
                Flat
              </button>
            </div>
            <FieldError message={depositError} />
          </label>
          <div>
            <small>Due at booking</small>
            <strong>
              {due} {cur}
            </strong>
          </div>
          <div>
            <small>Remaining at event</small>
            <strong>
              {remaining} {cur}
            </strong>
          </div>
        </div>
      ) : null}
    </Section>
  );
};

// ---- Items & add-ons ------------------------------------------------------------------------------------------------------------

const QtyControl = ({ value, min = 0, onChange, label }) => {
  const n = parseInt(value, 10) || 0;
  return (
    <div className="eb-qty-control" aria-label={label}>
      <button type="button" onClick={() => onChange(Math.max(min, n - 1))} aria-label="Decrease quantity" disabled={n <= min}>
        -
      </button>
      <span>{n}</span>
      <button type="button" onClick={() => onChange(n + 1)} aria-label="Increase quantity">
        +
      </button>
    </div>
  );
};

const ItemsSection = () => {
  const { state, d, set, setPricing, setAddOns, issues, hasFeature } = usePayments();
  const cur = currency(state);
  const pricing = d.pricing || [];
  const addOns = d.add_ons || [];
  const addOnsAllowed = hasFeature(FEATURE_IDS.UPSELL_CROSS_SELL);
  const pricingError = issueFor(issues, "pricing");
  const addOnError = issueFor(issues, "add_ons");

  const patchPricing = (i, patch) => {
    const next = pricing.map((row, k) => (k === i ? { ...row, ...patch } : row));
    // pricing[0].item_name follows the event name until edited here (6.C block 7)
    if (i === 0 && "item_name" in patch) set({ pricing: next, pricingNameTouched: true });
    else setPricing(next);
  };
  const patchAddOn = (i, patch) => setAddOns(addOns.map((row, k) => (k === i ? { ...row, ...patch } : row)));

  const row = (item, i, { addon }) => (
    // `.addon-row` = the 4-column variant (name · price · qty · Remove); extra price items use it too
    <div className={addon || i > 0 ? "addon-row" : ""} key={`${addon ? "a" : "p"}-${i}`}>
      <input
        type="text"
        value={item.item_name || ""}
        placeholder={addon ? "Add-on name" : "Item name"}
        onChange={(e) => (addon ? patchAddOn(i, { item_name: e.target.value }) : patchPricing(i, { item_name: e.target.value }))}
        aria-label={addon ? `Add-on ${i + 1} name` : `Price item ${i + 1} name`}
        disabled={addon && !addOnsAllowed}
      />
      <div className="eb-money-field compact">
        <span>{cur}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={item.price === undefined || item.price === null ? "" : item.price}
          onChange={(e) => (addon ? patchAddOn(i, { price: e.target.value }) : patchPricing(i, { price: e.target.value }))}
          aria-label={addon ? `Add-on ${i + 1} price` : `Price item ${i + 1} price`}
          disabled={addon && !addOnsAllowed}
        />
      </div>
      <QtyControl value={item.qty} min={addon ? 0 : 1} label="Quantity" onChange={(qty) => (addon ? patchAddOn(i, { qty }) : patchPricing(i, { qty }))} />
      {addon ? (
        <button type="button" className="text-danger" onClick={() => setAddOns(addOns.filter((_, k) => k !== i))}>
          Remove
        </button>
      ) : i > 0 ? (
        <button type="button" className="text-danger" onClick={() => setPricing(pricing.filter((_, k) => k !== i))}>
          Remove
        </button>
      ) : null}
    </div>
  );

  return (
    <Section
      id="paid-items-setting"
      title="Items & add-ons"
      text="Show what is included and offer optional extras."
      action={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!addOnsAllowed ? <PlanBadge kind="upgrade" /> : null}
          <button type="button" className="eb-outline-small" disabled={!addOnsAllowed} onClick={() => addOnsAllowed && setAddOns([...addOns, { item_name: "", price: "", qty: 0 }])}>
            + Add add-on
          </button>
        </span>
      }
    >
      <div className="eb-paid-items">
        {pricing.map((item, i) => row(item, i, { addon: false }))}
        <div>
          <button type="button" className="eb-link-btn" onClick={() => setPricing([...pricing, { item_name: "", price: "", qty: 1 }])}>
            + Add price item
          </button>
        </div>
        {addOns.map((item, i) => row(item, i, { addon: true }))}
      </div>
      <FieldError message={pricingError} />
      <FieldError message={addOnError} />
      {!addOnsAllowed && addOns.some((a) => String(a.item_name || "").trim() !== "") ? (
        <DowngradeNote>Add-ons are not in your plan &mdash; they are not saved with this event until you upgrade.</DowngradeNote>
      ) : null}
    </Section>
  );
};

// ---- Options ---------------------------------------------------------------------------------------------------------------------

const OptionsSection = () => {
  const { state, d, set, setPartial, issues, hasFeature } = usePayments();
  const cur = currency(state);
  const prepaid = d.event_prepaid_status === E;
  const partialOn = prepaid && d.payments_type === PAYMENT_TYPE.PARTIAL;
  const couponsAllowed = hasFeature(FEATURE_IDS.COUPON_CODES);
  const partialError = touched(state) ? issueFor(issues, "payments_price") : null;
  const rateError = touched(state) ? issueFor(issues, "staff_commission_rate") : null;
  const flip = (field) => set({ [field]: d[field] === E ? D : E });

  return (
    <div className="eb-paid-section split-options" id="payment-options-setting">
      <SettingLine compact title="Partial payment" text="Let invitees split the amount into more than one payment.">
        <Switch on={partialOn} disabled={!prepaid} label="Partial payment" onChange={setPartial} />
      </SettingLine>
      {!prepaid ? <Helper>Available when payment is collected at booking.</Helper> : null}
      {partialOn ? (
        <>
          <div className="eb-coupon-inline">
            <span>First payment</span>
            <select value={d.payments_flat_type || FLAT_PERCENT.PERCENT} onChange={(e) => set({ payments_flat_type: e.target.value })} aria-label="First payment type">
              <option value={FLAT_PERCENT.PERCENT}>Percent (%)</option>
              <option value={FLAT_PERCENT.FLAT}>Flat ({cur})</option>
            </select>
            <input
              type="number"
              min="1"
              step="0.01"
              value={d.payments_price === undefined ? "" : d.payments_price}
              placeholder="Amount"
              onChange={(e) => set({ payments_price: e.target.value })}
              aria-label="First payment amount"
              className={partialError ? "eb-has-error" : ""}
            />
          </div>
          <FieldError message={partialError} />
        </>
      ) : null}

      <SettingLine compact title="Coupon codes" text="Allow invitees to enter a discount code.">
        {!couponsAllowed ? <PlanBadge kind="upgrade" /> : null}
        <Switch on={d.coupon_codes_status === E} disabled={!couponsAllowed} label="Coupon codes" onChange={() => flip("coupon_codes_status")} />
      </SettingLine>
      {couponsAllowed && d.coupon_codes_status === E ? (
        <Helper>
          Manage codes under <Link to={ROUTES.COUPONS}>Settings &rsaquo; Discounts</Link>.
        </Helper>
      ) : null}
      {!couponsAllowed && d.coupon_codes_status === E ? <DowngradeNote>Coupon codes are not in your plan &mdash; turned off the next time this section is saved.</DowngradeNote> : null}

      <SettingLine compact title="Recurring booking discount" text="Discount bookings made as part of a recurring series.">
        <Switch on={d.recurrent_booking_discounts_status === E} label="Recurring discount" onChange={() => flip("recurrent_booking_discounts_status")} />
      </SettingLine>

      <SettingLine compact id="commission-setting" title="Commission status" text="Apply your configured platform commission to this event.">
        <Switch on={d.staff_commission_status === E} label="Commission status" onChange={() => flip("staff_commission_status")} />
      </SettingLine>
      {d.staff_commission_status === E ? (
        <>
          <div className="eb-coupon-inline">
            <span>Rate</span>
            <input
              type="number"
              min="1"
              max="99"
              value={d.staff_commission_rate === undefined || d.staff_commission_rate === null ? "" : d.staff_commission_rate}
              onChange={(e) => set({ staff_commission_rate: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0 })}
              aria-label="Commission rate"
              className={rateError ? "eb-has-error" : ""}
            />
            <span>%</span>
          </div>
          <FieldError message={rateError} />
        </>
      ) : null}
    </div>
  );
};

// ---- Refunds --------------------------------------------------------------------------------------------------------------------------

/** Preset select + `Custom…` (days / hours / minutes) → total minutes, as `components/Time` (TimeField) emits today. */
const RefundCutoff = ({ value, onChange }) => {
  const raw = value === "" || value === null || value === undefined ? "" : parseInt(value, 10) || 0;
  const known = raw !== "" && REFUND_CUTOFF_OPTIONS.some((o) => o.value === raw);
  const [custom, setCustom] = useState(raw !== "" && !known);
  const total = raw === "" ? 0 : raw;
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  const emit = (dd, hh, mm) => onChange(Math.max(0, dd) * 1440 + Math.max(0, hh) * 60 + Math.max(0, mm));
  const showCustom = custom || (raw !== "" && !known);

  return (
    <label>
      Refund cut-off time
      <select
        value={showCustom ? "custom" : raw === "" ? "" : String(raw)}
        onChange={(e) => {
          if (e.target.value === "custom") {
            setCustom(true);
            return;
          }
          setCustom(false);
          onChange(parseInt(e.target.value, 10) || 0);
        }}
      >
        {raw === "" ? <option value="">Choose a cut-off</option> : null}
        {REFUND_CUTOFF_OPTIONS.map((o) => (
          <option key={o.value} value={String(o.value)}>
            {o.label}
          </option>
        ))}
        <option value="custom">{"Custom\u2026"}</option>
      </select>
      {showCustom ? (
        <span className="eb-rule-custom eb-cutoff-custom">
          <input type="number" min="0" value={days} onChange={(e) => emit(parseInt(e.target.value, 10) || 0, hours, minutes)} aria-label="Days" />
          <small>days</small>
          <input type="number" min="0" max="23" value={hours} onChange={(e) => emit(days, parseInt(e.target.value, 10) || 0, minutes)} aria-label="Hours" />
          <small>hours</small>
          <input type="number" min="0" max="59" value={minutes} onChange={(e) => emit(days, hours, parseInt(e.target.value, 10) || 0)} aria-label="Minutes" />
          <small>minutes</small>
        </span>
      ) : null}
    </label>
  );
};

const RefundSection = () => {
  const { state, d, set, issues } = usePayments();
  const pctError = touched(state) ? issueFor(issues, "refund_percentage") : null;
  return (
    <Section id="refund-setting" title="Cancellation, instructions & refunds" text="Tell invitees what to expect and when a refund is available.">
      <div className="eb-form-settings-grid">
        <label>
          Refund type
          <select
            value={d.refund_type || ""}
            onChange={(e) => set({ refund_type: e.target.value, ...(e.target.value !== REFUND_TYPE.PARTIAL ? { refund_percentage: "" } : {}) })}
            aria-label="Refund type"
          >
            {!d.refund_type ? <option value="">Choose a refund type</option> : null}
            <option value={REFUND_TYPE.NONE}>No refunds</option>
            <option value={REFUND_TYPE.FULL}>Full refund</option>
            <option value={REFUND_TYPE.PARTIAL}>Partial refund</option>
          </select>
        </label>
        <RefundCutoff value={d.refund_cutoff_time} onChange={(minutes) => set({ refund_cutoff_time: minutes })} />
        {d.refund_type === REFUND_TYPE.PARTIAL ? (
          <label>
            Refund percentage
            <input
              type="number"
              min="0"
              max="100"
              value={d.refund_percentage === undefined || d.refund_percentage === null ? "" : d.refund_percentage}
              onChange={(e) => {
                // today's page clamps to 100
                const v = e.target.value;
                set({ refund_percentage: v === "" ? "" : Math.min(100, Number(v)) });
              }}
              aria-label="Refund percentage"
              className={pctError ? "eb-has-error" : ""}
            />
            <FieldError message={pctError} />
          </label>
        ) : null}
      </div>
      <div style={{ marginTop: 14 }}>
        <PolicyFields instructionsPlaceholder="Add payment, arrival, or preparation instructions." />
      </div>
    </Section>
  );
};

// ---- Section --------------------------------------------------------------------------------------------------------------------------

const PaymentsSection = () => (
  <SettingsView icon="card" title="Payments" text="Pricing, deposits, discounts, add-ons, and refund rules.">
    <div className="eb-paid-settings-full">
      <ProviderOverview />
      <PriceSection />
      <TimingSection />
      <ItemsSection />
      <OptionsSection />
      <RefundSection />
    </div>
  </SettingsView>
);

export default PaymentsSection;
